<template>
  <div class="modelReg delivery-rules-modal px-4 py-6">
    <div class="modelReg__grid delivery-rules-modal__content space-y-6">
      <!-- Заголовок -->
      <div class="flex items-center gap-2">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Условия доставки
        </h3>
      </div>

      <!-- Информация о минимальной сумме заказа -->
      <div v-if="minOrderAmount > 0 && cartAmount < minOrderAmount" class="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0">
            <UIcon name="i-heroicons-information-circle" class="w-5 h-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <span class="text-sm font-medium text-orange-900 dark:text-orange-100">
              Минимальная сумма заказа
            </span>
            <span class="text-sm text-orange-700 dark:text-orange-300 mt-1">
              {{ minOrderAmount }} ₽
            </span>
          </div>
        </div>
      </div>

      <!-- Правила стоимости доставки -->
      <div v-if="sortedDeliveryRules.length > 0">
        <div class="text-md font-semibold text-gray-900 dark:text-white mb-6">
          Стоимость доставки по сумме заказа:
        </div>
        
        <div class="space-y-2">
          <div
            v-for="(rule, index) in sortedDeliveryRules"
            :key="index"
            class="flex items-center justify-between p-3 rounded-lg transition-colors"
            :class="isCurrentRule(rule) 
              ? 'bg-purple-50 dark:bg-purple-900/30 border-2 border-purple-300 dark:border-purple-700' 
              : 'bg-gray-50 dark:bg-gray-800 border-2 border-transparent'"
          >
            <div class="flex items-center gap-2">
              <UIcon 
                v-if="isCurrentRule(rule)" 
                name="i-heroicons-check-circle-20-solid" 
                class="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0"
              />
              <span 
                class="text-sm"
                :class="isCurrentRule(rule) 
                  ? 'font-semibold text-purple-900 dark:text-purple-100' 
                  : 'text-gray-700 dark:text-gray-300'"
              >
                {{ formatRuleRange(rule) }}
              </span>
            </div>
            
            <span 
              class="text-sm font-bold"
              :class="rule.delivery_cost === 0 
                ? 'text-green-600 dark:text-green-400' 
                : isCurrentRule(rule)
                  ? 'text-purple-900 dark:text-purple-100'
                  : 'text-gray-900 dark:text-gray-100'"
            >
              {{ rule.delivery_cost === 0 ? 'Бесплатно' : `${rule.delivery_cost} ₽` }}
            </span>
          </div>
        </div>
      </div>

      <!-- Текущая стоимость доставки -->
      <div 
        v-if="currentRule"
        class="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/40 dark:to-pink-900/40 rounded-lg p-4 border-2 border-purple-200 dark:border-purple-800 "
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-gray-900 dark:text-white">
            Стоимость вашей доставки:
          </span>
          <span class="text-lg font-bold text-purple-700 dark:text-purple-300">
            {{ currentRule.delivery_cost === 0 ? 'Бесплатно' : `${currentRule.delivery_cost} ₽` }}
          </span>
        </div>
      </div>

      <!-- Подсказка о бесплатной доставке -->
      <div v-if="toFreeDelivery !== null && toFreeDelivery > 0" class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0">
            <UIcon name="i-heroicons-light-bulb" class="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p class="text-sm font-medium text-blue-900 dark:text-blue-100">
              Добавьте ещё {{ toFreeDelivery }} ₽ и получите бесплатную доставку!
            </p>
          </div>
        </div>
      </div>

      <!-- Свичер "Больше не показывать" -->
      <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <UIcon name="i-heroicons-eye-slash" class="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <span class="text-sm text-gray-700 dark:text-gray-300">
              Больше не показывать это окно
            </span>
          </div>
          <UToggle 
            v-model="hideModal"
            @update:model-value="handleToggleHide"
            color="primary"
            size="sm"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useModalStore } from '@shared/model'
import { useDeliveryRulesStorage } from '@entities/institution'
import { getCurrentTimeHHMM, isTimeInRange, extractTimeFromISO } from '@features/map'

const modalStore = useModalStore()
const deliveryRulesStorage = useDeliveryRulesStorage()

// Состояние свичера
const hideModal = ref(false)

// Получаем данные из modalStore.modalData
const cartAmount = computed(() => modalStore.modalData.cartAmount || 0)
const minOrderAmount = computed(() => modalStore.modalData.minOrderAmount || 0)
const deliveryRules = computed(() => modalStore.modalData.deliveryRules || [])

// Текущее время в формате HH:MM
const currentTime = computed(() => getCurrentTimeHHMM())

// Фильтруем правила по текущему времени суток
const filteredDeliveryRules = computed(() => {
  if (!Array.isArray(deliveryRules.value)) return []
  
  const now = currentTime.value
  
  return deliveryRules.value.filter((rule: any) => {
    // Извлекаем время из ISO строк
    const fromTime = extractTimeFromISO(rule.from_time)
    const toTime = extractTimeFromISO(rule.to_time)
    
    // Проверяем, попадает ли текущее время в диапазон правила
    return isTimeInRange(now, fromTime, toTime)
  })
})

const closeModal = () => {
  modalStore.close()
}

// Загружаем состояние свичера при монтировании
onMounted(async () => {
  hideModal.value = await deliveryRulesStorage.isDeliveryRulesHidden()
})

// Обработчик изменения свичера
const handleToggleHide = async (value: boolean) => {
  hideModal.value = value
  await deliveryRulesStorage.setDeliveryRulesHidden(value)
  
  if (value) {
    // Если пользователь выбрал "больше не показывать", закрываем модальное окно
    closeModal()
  }
}

// Отсортированные правила доставки (по возрастанию min_order_amount)
// Теперь используем filteredDeliveryRules вместо deliveryRules для учета времени суток
const sortedDeliveryRules = computed(() => {
  if (!Array.isArray(filteredDeliveryRules.value)) return []
  return [...filteredDeliveryRules.value].sort((a: any, b: any) => {
    const minA = a.min_order_amount ?? a.price_range_from ?? 0
    const minB = b.min_order_amount ?? b.price_range_from ?? 0
    return minA - minB
  })
})

// Форматирование диапазона суммы для правила
const formatRuleRange = (rule: any) => {
  // Поддерживаем обе структуры данных: старую (min_order_amount/max_order_amount) и новую (price_range_from/price_range_to)
  const min = rule.min_order_amount ?? rule.price_range_from ?? 0
  const max = rule.max_order_amount ?? rule.price_range_to ?? null
  
  if (max === null || max === undefined) {
    return `От ${min} ₽`
  }
  return `От ${min} до ${max} ₽`
}

// Проверка, является ли правило текущим для заказа
const isCurrentRule = (rule: any) => {
  // Поддерживаем обе структуры данных
  const min = rule.min_order_amount ?? rule.price_range_from ?? 0
  const max = rule.max_order_amount ?? rule.price_range_to ?? null
  const maxValue = max === null || max === undefined ? Infinity : max
  return cartAmount.value >= min && cartAmount.value <= maxValue
}

// Текущее правило
const currentRule = computed(() => {
  return sortedDeliveryRules.value.find(rule => isCurrentRule(rule)) || null
})

// Правило бесплатной доставки (если есть)
const freeRule = computed(() => 
  sortedDeliveryRules.value.find((rule: any) => rule.delivery_cost === 0) || null
)

// Сколько не хватает до бесплатной доставки, либо null если её не бывает
const toFreeDelivery = computed(() => {
  if (!freeRule.value) return null
  // Поддерживаем обе структуры данных
  const minAmount = freeRule.value.min_order_amount ?? freeRule.value.price_range_from ?? 0
  return Math.max(minAmount - cartAmount.value, 0)
})
</script>